import React, { Component } from 'react';

import dashboardService from '../../publisher/dashboard/dashboard-service';
import { dateService } from '../../../services/date';

import { DateRangeDropdown } from '../../base/megatable/models';
import DateRangesDropdown from '../../base/dropdowns/dateranges-dropdown';
import Graph from '../../publisher/dashboard/graph';
import { Loader, NoResult } from '../../misc/loader-utils';

import '../../publisher/dashboard/earnings-graph.scss';

class EarningsGraph extends Component {
  constructor(props) {
    super(props);

    this.service = dashboardService;
    this.dateService = dateService;

    this.presetRanges = this.dateService.getPresetDateRanges();
    this.rangeDropdownItems = new DateRangeDropdown().items;
    const defaultRange = this.rangeDropdownItems.find(item => item.selected);

    this.graphTypes = ['revenue', 'spend', 'clicks', 'rpc'];
    this.graphLabelMap = {
      revenue: 'Revenue',
      spend: 'Spend',
      clicks: 'Clicks',
      rpc: 'RPC',
    };
    this.defaultStat = {
      revenue: 0,
      spend: 0,
      clicks: 0,
      rpc: 0,
      roa: 0,
    };
    this.spendDisabledRanges = ['sevenDay', 'lastMonth', 'thisMonth'];

    this.state = {
      loading: true,
      showNoResult: false,
      selectedRange: defaultRange,
      graphType: this.graphTypes[0],
      graphDataByRange: {
        sevenDay: [],
        thisMonth: [],
        lastMonth: [],
        halfYear: [],
        thisYear: [],
        multiYear: [],
      },
    };
  }

  componentDidMount() {
    this.getEarningsStats();
  }

  getEarningsStats() {
    const dayRange = this.presetRanges.last2Months;
    const monthRange = this.presetRanges[this.getMonthRange()];
    const yearRange = this.presetRanges.multiYear;

    const queries = [
      this.service.getMerchantStatsByDay(this.props.merchId, this.getRequestParams(dayRange)),
      this.service.getMerchantStatsByMonth(this.props.merchId, this.getRequestParams(monthRange)),
      this.service.getMerchantStatsByYear(this.props.merchId, this.getRequestParams(yearRange)),
    ];

    Promise.all(queries)
      .then(results => {
        const dailyData = results[0];
        const monthlyData = results[1];
        const yearlyData = results[2];

        const graphDataByRange = {
          sevenDay: this.processGraphData(dailyData, 'sevenDay'),
          halfYear: this.processGraphData(monthlyData, 'halfYear'),
          thisMonth: this.processGraphData(dailyData, 'thisMonth'),
          lastMonth: this.processGraphData(dailyData, 'lastMonth'),
          thisYear: this.processGraphData(monthlyData, 'thisYear'),
          multiYear: yearlyData ? this.processGraphData(yearlyData, 'multiYear') : [],
        };

        this.setState({
          loading: false,
          showNoResult: false,
          graphDataByRange,
        });
      })
      .catch(() => {
        this.handleStatError();
      });
  }

  handleStatError() {
    this.setState({ loading: false, showNoResult: true });
  }

  updateRange(rangeDropdownItems) {
    const { graphType } = this.state;
    const selectedRange = rangeDropdownItems.find(item => item.selected);

    this.setState({
      selectedRange,
      graphType,
      rangeDropdownItems,
    });
  }

  /* Data manipulation */
  processGraphData(stats, rangeType) {
    /*
     * rangeType: sevenDay|thisMonth|lastMonth|halfYear|thisYear|multiYear
     * Generate date labels and match them to stats
     */
    const rangeObject = this.rangeDropdownItems.find(item => item.name === rangeType);
    const dateRange = this.presetRanges[rangeObject.name];
    const timeUnit = rangeObject.unit;

    const dateLabels = this.service.generateDatesForRange(
      dateRange.startDate,
      dateRange.endDate,
      timeUnit,
      rangeType,
    );
    const statsWithLabels = this.matchStatsToLabels(stats, dateLabels, rangeType);

    return this.service.formatDateLabels(statsWithLabels, timeUnit);
  }

  matchStatsToLabels(data, dateLabels, rangeType) {
    const compare = (stats, dateLabel) => {
      if (rangeType === 'multiYear') {
        return stats.event_year === dateLabel;
      }
      if (['halfYear', 'thisYear'].includes(rangeType)) {
        return stats.event_month === dateLabel;
      }
      return stats.event_date === dateLabel;
    };

    return dateLabels.map(dateLabel => {
      // Match date label to the most relevant stat or default to empty stat
      const relevantStats = data.filter(stat => compare(stat, dateLabel));
      let matchedStat = relevantStats[0];

      // If there are multiple matches, look for the most recent data point (by date)
      if (relevantStats.length > 1) {
        relevantStats.forEach(stat => {
          const statIsNewer =
            this.dateService.compareDates(matchedStat.event_month, stat.event_month) > 0;
          if (statIsNewer) matchedStat = stat;
        });
      }

      const stats = matchedStat || this.defaultStat;
      return { dateLabel, stats };
    });
    /*
     * [{dateLabel: '2019-10-01', stats: {clicks: 8000, ...}}, ...]
     */
  }

  /* Helper functions */
  getRequestParams(dateRange) {
    return {
      date_from: this.dateService.formatDate(dateRange.startDate),
      date_to: this.dateService.formatDate(dateRange.endDate),
    };
  }

  getMonthRange() {
    // Use yearToDate if current month is greater than June
    const currentMonth = new Date().getMonth();
    return currentMonth > 5 ? 'thisYear' : 'halfYear';
  }

  render() {
    const rangeType = this.state.selectedRange.name;

    const graphTypes = this.graphTypes.map((graphType, i) => {
      const selected = graphType === this.state.graphType ? ' selected' : '';
      return (
        <div
          key={i}
          className={`graph-view ${graphType}${selected}`}
          onClick={() => this.setState({ graphType })}
        >
          <span className={`color-block ${graphType}`}></span>
          <span className="text">{this.graphLabelMap[graphType]}</span>
        </div>
      );
    });

    const dateRanges = (
      <DateRangesDropdown
        gaEvent={{
          category: 'Merchant Dashboard',
          label: `${this.state.graphType} Date Range`,
        }}
        items={this.rangeDropdownItems}
        updateRange={updatedItems => this.updateRange(updatedItems)}
      />
    );

    const graph =
      !this.state.loading && !this.state.showNoResult ? (
        <Graph
          data={this.state.graphDataByRange[rangeType]}
          projections={[]}
          graphType={this.state.graphType}
          rangeType={rangeType}
          graphLabelMap={this.graphLabelMap}
          merchantGraph={true}
        />
      ) : null;

    return (
      <div className="earnings-graph merchant">
        <div className="header">
          <div className="legend">{graphTypes}</div>
          <div className="options">{dateRanges}</div>
        </div>
        <Loader loading={this.state.loading} />
        <NoResult type="earnings-graph" noResult={!this.state.loading && this.state.showNoResult} />
        {graph}
      </div>
    );
  }
}

export default EarningsGraph;
